import BalanceRow from '@components/BalanceRow'
import BottomSheet from '@components/BottomSheet'
import CandleChart from '@components/CandleChart'
import { Titlebar } from '@components/Titlebar/Titlebar'
import TokenAvatar from '@components/TokenAvatar'
import { PERP_FUNDING_SOURCE_IDS } from '@plug-core/constants/business'
import { USDC_HYPERCORE_PERP_ID } from '@plug-core/constants/tokenIds'
import useCreateOrder from '@plug-core/hooks/perps/useCreateOrder'
import usePerpAvailableBalance from '@plug-core/hooks/perps/usePerpAvailableBalance'
import usePerpsDetails, {
  PERP_CHART_RANGES,
  PerpChartRange,
} from '@plug-core/hooks/perps/usePerpsDetails'
import { logger } from '@plug-core/logger'
import { formatMoney, formatNumber } from '@plug-core/utils/formatNumber'
import { Button, Heading, ListGroup, ListRow, RowTile, SegmentedControl, Surface, Text } from '@ui'
import { ROUTES } from '@utils/routes'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiMinus, HiPlus } from 'react-icons/hi'
import { HiArrowTrendingDown, HiArrowTrendingUp } from 'react-icons/hi2'
import { useNavigate, useParams } from 'react-router-dom'
import CreateOrder from './CreateOrder'
import OrderInfoItem from './OrderInfoItem'

const PerpDetails = () => {
  const { hyperliquidTokenId } = useParams<{ hyperliquidTokenId: string }>()
  const [range, setRange] = useState<PerpChartRange>(PERP_CHART_RANGES[0])
  const [modifyOrderSheetVisible, setModifyOrderSheetVisible] = useState(false)
  const [closeOrderSheetVisible, setCloseOrderSheetVisible] = useState(false)
  const [createOrderSheetVisible, setCreateOrderSheetVisible] = useState(false)
  const [orderType, setOrderType] = useState<'long' | 'short'>('long')
  const [editType, setEditType] = useState<'increase' | 'reduce' | undefined>(undefined)

  const navigate = useNavigate()
  const { t } = useTranslation()

  const { token, candles, price, priceChange, currentPosition, positionDetails, perpTokenInfo } =
    usePerpsDetails(parseInt(hyperliquidTokenId!), range)

  const availableBalance = usePerpAvailableBalance()

  const { closePosition, loading } = useCreateOrder({
    hyperliquidTokenId: parseInt(hyperliquidTokenId!),
    currentOrder: currentPosition,
  })

  const changeTone = (priceChange?.percentage || 0) >= 0 ? 'text-success' : 'text-danger'

  const renderInfoCard = (title: string, value?: string, negative?: boolean) => (
    <Surface className="min-w-[120px]">
      <Text variant="caption" tone="fg-2" className="mb-1">
        {title}
      </Text>
      <Text weight="semibold" tone={negative ? 'danger' : 'fg'} tabular>
        {value}
      </Text>
    </Surface>
  )

  const renderPosition = () => {
    if (!positionDetails?.cards) return null

    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <Heading level={4} className="mb-2">
            {t('perps.details.yourPosition')}
          </Heading>
          <div className="grid grid-cols-2 gap-4">
            {positionDetails.cards.map(({ title, value, negative }, index) => (
              <div key={index}>{renderInfoCard(title, value, negative)}</div>
            ))}
          </div>
        </div>
        {positionDetails.table && (
          <ListGroup>
            {positionDetails.table.map((row) => (
              <OrderInfoItem key={row.title} attribute={row.title}>
                {row.value}
              </OrderInfoItem>
            ))}
          </ListGroup>
        )}
      </div>
    )
  }

  const renderButtons = () => {
    if (!currentPosition) {
      return (
        <div className="flex gap-4 py-2">
          {/* Long = success tone, short = danger tone. */}
          <Button
            variant="tint-success"
            startIcon={<HiArrowTrendingUp className="size-5" />}
            fullWidth
            className="flex-1"
            onClick={() => {
              setOrderType('long')
              setEditType(undefined)
              setCreateOrderSheetVisible(true)
            }}
          >
            {t('perps.type.long')}
          </Button>
          <Button
            variant="tint-danger"
            startIcon={<HiArrowTrendingDown className="size-5" />}
            fullWidth
            className="flex-1"
            onClick={() => {
              setOrderType('short')
              setEditType(undefined)
              setCreateOrderSheetVisible(true)
            }}
          >
            {t('perps.type.short')}
          </Button>
        </div>
      )
    }

    return (
      <div className="flex gap-4 py-2">
        <Button
          variant="secondary"
          fullWidth
          className="flex-1"
          onClick={() => setModifyOrderSheetVisible(true)}
        >
          {t('common.modify')}
        </Button>
        <Button
          variant="primary"
          fullWidth
          className="flex-1"
          onClick={() => setCloseOrderSheetVisible(true)}
        >
          {t('common.close')}
        </Button>
      </div>
    )
  }

  const renderManagePerpRow = (editType: 'increase' | 'reduce') => (
    <ListRow
      inset
      leading={
        <RowTile className="text-fg">
          {editType === 'increase' ? <HiPlus size={24} /> : <HiMinus size={24} />}
        </RowTile>
      }
      primary={t(`perps.editAction.${editType}.title`)}
      secondary={t(`perps.editAction.${editType}.description`)}
      onClick={() => {
        setModifyOrderSheetVisible(false)
        setEditType(editType)
        setCreateOrderSheetVisible(true)
      }}
    />
  )

  return (
    <div className="mb-3 flex flex-1 flex-col gap-4">
      <Titlebar onActionClick={() => navigate(-1)} />

      <ListGroup className="-mt-4">
        <ListRow
          className="min-h-[72px]"
          leading={<TokenAvatar logo={token?.logo} size={44} />}
          primary={`${token?.symbol}-USD`}
          trailing={
            typeof priceChange !== 'undefined' && (
              <span className="flex flex-col items-end">
                <Text as="span" weight="semibold" tone="fg" tabular>
                  {formatMoney(price, undefined, { format: 'precise' })}
                </Text>
                <Text as="span" variant="body-s" tabular className={changeTone}>
                  {formatNumber(priceChange.percentage, '%', {
                    signDisplay: 'exceptZero',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Text>
              </span>
            )
          }
        />
      </ListGroup>

      <CandleChart
        candles={candles || []}
        entry={currentPosition ? parseFloat(currentPosition.position.entryPx) : undefined}
        liquidation={
          currentPosition?.position.liquidationPx
            ? parseFloat(currentPosition.position.liquidationPx)
            : undefined
        }
        height={200}
      />

      <SegmentedControl
        className="self-center"
        size="sm"
        aria-label="Chart range"
        value={range.title}
        onChange={(title) => {
          const next = PERP_CHART_RANGES.find((r) => r.title === title)
          if (next) setRange(next)
        }}
        options={PERP_CHART_RANGES.map((r) => ({ value: r.title, label: r.title }))}
      />

      {currentPosition && renderPosition()}

      {!currentPosition && (
        <BalanceRow
          logo="https://plug-cdn.s3.amazonaws.com/chain-logos/hyperliquid.png"
          title={t('perps.availableToTrade')}
          subtitle={`${formatMoney(availableBalance)} available`}
          value={t('common.add')}
          onPress={() =>
            navigate(ROUTES.SWAP, {
              state: {
                toTokenId: USDC_HYPERCORE_PERP_ID,
                lockToToken: true,
                sourceTokenIds: PERP_FUNDING_SOURCE_IDS,
              },
            })
          }
        />
      )}

      {perpTokenInfo && (
        <div className="flex flex-col">
          <Heading level={4} className="mb-2">
            Info
          </Heading>
          <ListGroup>
            {perpTokenInfo.map((row) => (
              <OrderInfoItem key={row.title} attribute={row.title}>
                {row.value}
              </OrderInfoItem>
            ))}
          </ListGroup>
        </div>
      )}

      {/* Glass footer: blurs what scrolls beneath it instead of painting a solid band over the art. */}
      <div className="sticky inset-x-0 bottom-0 py-2">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -inset-x-3 -top-4 bottom-0 backdrop-blur-md [mask-image:linear-gradient(to_bottom,transparent,#000_40%)] sm:-inset-x-5 lg:inset-x-0"
        />
        <div className="relative">{renderButtons()}</div>
      </div>

      <BottomSheet
        visible={modifyOrderSheetVisible}
        onBackdropClick={() => setModifyOrderSheetVisible(false)}
        title={t('common.modify')}
      >
        <div className="flex flex-col gap-4">
          {renderManagePerpRow('increase')}
          {renderManagePerpRow('reduce')}
        </div>
      </BottomSheet>

      <BottomSheet
        visible={closeOrderSheetVisible}
        onBackdropClick={() => setCloseOrderSheetVisible(false)}
        title={t('perps.details.closePosition')}
      >
        <div className="flex flex-col gap-4">
          {currentPosition && (
            <ListGroup>
              <OrderInfoItem attribute={t('perps.details.size')}>
                {formatNumber(
                  parseFloat(currentPosition.position.szi),
                  currentPosition.position.coin
                )}
              </OrderInfoItem>
              <OrderInfoItem attribute={t('perps.details.pnl')}>
                {formatMoney(parseFloat(currentPosition.position.unrealizedPnl), undefined, {
                  minimumFractionDigits: 4,
                })}
              </OrderInfoItem>
            </ListGroup>
          )}
          <div className="flex gap-4">
            <Button
              variant="secondary"
              fullWidth
              className="flex-1"
              onClick={() => setCloseOrderSheetVisible(false)}
            >
              {t('common.cancel')}
            </Button>

            <Button
              variant="primary"
              fullWidth
              className="flex-1"
              onClick={() => {
                closePosition()
                  .then(() => {
                    setCloseOrderSheetVisible(false)
                  })
                  .catch(logger.error)
              }}
              loading={loading}
            >
              {t('perps.details.closePosition')}
            </Button>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet
        visible={createOrderSheetVisible}
        onBackdropClick={() => {
          setCreateOrderSheetVisible(false)
          setEditType(undefined)
        }}
      >
        <CreateOrder
          hyperliquidTokenId={parseInt(hyperliquidTokenId || '')}
          type={orderType}
          editType={editType}
          currentOrder={currentPosition}
          onClose={() => {
            setCreateOrderSheetVisible(false)
            setEditType(undefined)
          }}
          onSuccess={() => {
            setCreateOrderSheetVisible(false)
            setEditType(undefined)
          }}
        />
      </BottomSheet>
    </div>
  )
}

export default PerpDetails
